/**
 * BodySpec on the patient's Scans/Other section (SCRUM-2217, Robin: "BodySpec
 * is not a lab"). It used to be a sub-tab of Labs; it is now a block under
 * the Scans/Other documents, with its own "Book BodySpec Scan" button, the
 * patient's bookings, and their Finish payment / Cancel actions.
 *
 * What stayed the same: the booking modal, the Forward charge for a
 * charge_patient booking and the cancel call — the charge and cancel come
 * from useIntegrationOrderCharge, shared with the Labs tab. What changed with
 * the move: booking no longer switches to a BodySpec tab (there is none), the
 * cancel toast says "Scan cancelled.", and the empty list has its own text.
 *
 * Renders nothing when the practice does not have BodySpec.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import {
  getEnabledIntegrations,
  type CreateIntegrationOrderResult,
} from '@/services/integrations.service';
import { Button } from '@/components/ui/Button';
import { BodySpecOrderModal } from './labs/BodySpecOrderModal';
import { IntegrationOrdersSubTab } from './labs/IntegrationOrdersSubTab';
import { integrationOrdersKey, useIntegrationOrderCharge } from './labs/useIntegrationOrderCharge';
import { linkedOrderIdFor, type LinkedOrder } from './labs/linkedOrder';

const BODYSPEC_FULFILLMENT_LABELS: Record<string, string> = {
  awaiting_payment: 'Awaiting payment',
  pending_scheduling: 'Pending scheduling',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function BodySpecScansSection({ patientId, linkedOrder, onLinkedOrderOpened }: {
  patientId: number;
  /** A link straight to one order (linkedOrder.ts): opens its details on arrival. */
  linkedOrder?: LinkedOrder | null;
  onLinkedOrderOpened?: () => void;
}) {
  const queryClient = useQueryClient();
  const [orderOpen, setOrderOpen] = useState(false);
  const { startCharge, chargeExisting, cancelExisting, chargeModal } = useIntegrationOrderCharge(patientId);

  const { data: integrations = [] } = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    staleTime: 5 * 60_000,
  });
  const bodyspec = integrations.find((i) => i.key === 'bodyspec');
  if (!bodyspec) return null;

  const handleOrdered = (result: CreateIntegrationOrderResult) => {
    setOrderOpen(false);
    void queryClient.invalidateQueries({ queryKey: integrationOrdersKey('bodyspec', patientId) });
    if (result.billingMode === 'charge_patient') {
      startCharge(bodyspec, [{ orderId: result.integrationOrderId, amount: result.amount }]);
    }
  };

  return (
    // Spacing lives on the section, so a practice without BodySpec gets no gap.
    <section className="mt-6 space-y-3" aria-label="BodySpec scans">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Activity className="h-4 w-4 text-slate-400" /> BodySpec scans
        </h3>
        <Button size="sm" onClick={() => setOrderOpen(true)}>
          <Activity className="mr-1 h-3.5 w-3.5" />
          Book BodySpec Scan
        </Button>
      </div>

      <IntegrationOrdersSubTab
        patientId={patientId}
        integrationKey="bodyspec"
        hideCancelled
        fulfillmentLabels={BODYSPEC_FULFILLMENT_LABELS}
        emptyMessage="No BodySpec scans yet. Use “Book BodySpec Scan” to schedule one."
        onFinishPayment={(o) => chargeExisting(bodyspec, o)}
        onCancel={(o) => void cancelExisting(bodyspec, o, 'Scan')}
        openOrderId={linkedOrderIdFor(linkedOrder, 'bodyspec')}
        onOrderOpened={onLinkedOrderOpened}
      />

      <BodySpecOrderModal
        patientId={patientId}
        integration={bodyspec}
        isOpen={orderOpen}
        onClose={() => setOrderOpen(false)}
        onOrdered={handleOrdered}
      />

      {chargeModal}
    </section>
  );
}
